import React, { useCallback, useRef } from 'react';
import { Activity } from 'types/interfaces/activity';
import type { ActivityViewMode } from '../ActivityPage';
import ActivityFeedItem from './ActivityFeedItem';
import ActivityFeedListItem from './ActivityFeedListItem';
import './ActivityFeed.css';

interface ActivityFeedProps {
  activities: Activity[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isFetchingMore: boolean;
  newItemIds: Set<string>;
  clearNewItem: (id: string) => void;
  scrolledPendingCount: number;
  onShowNew: () => void;
  viewMode: ActivityViewMode;
}

const SKELETON_COUNT = 10;

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  isLoading,
  hasMore,
  loadMore,
  isFetchingMore,
  newItemIds,
  clearNewItem,
  scrolledPendingCount,
  onShowNew,
  viewMode,
}) => {
  const isListMode = viewMode === 'list';
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Infinite scroll via intersection observer on last item
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      if (!node || !hasMore) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
            loadMore();
          }
        },
        { threshold: 0.1 }
      );
      observerRef.current.observe(node);
    },
    [hasMore, loadMore, isFetchingMore]
  );

  // Loading skeleton — card-shaped shimmers in grid layout
  if (isLoading) {
    return (
      <div className={isListMode ? 'activity-feed activity-feed--list' : `activity-feed activity-feed--${viewMode}`}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <div key={i}>
            <div className="activity-feed__skeleton-card">
              <div className="activity-feed__skeleton-image" />
              <div className="activity-feed__skeleton-footer">
                <div className="activity-feed__skeleton-avatar" />
                <div className="activity-feed__skeleton-lines">
                  <div className="activity-feed__skeleton-text" />
                  <div className="activity-feed__skeleton-text activity-feed__skeleton-text--short" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <div className="activity-feed activity-feed--empty">
        <div className="activity-feed__empty">
          <p>No activity to show. Try adjusting your filters.</p>
        </div>
      </div>
    );
  }

  const feedClassName = isListMode
    ? 'activity-feed activity-feed--list'
    : `activity-feed activity-feed--${viewMode}`;

  return (
    <>
      <div className={feedClassName}>
        {scrolledPendingCount > 0 && (
          <button className="activity-feed__new-items-pill" onClick={onShowNew}>
            {scrolledPendingCount} new {scrolledPendingCount === 1 ? 'item' : 'items'}
          </button>
        )}
        {activities.map((activity, index) => {
          const isLast = index === activities.length - 1;
          return (
            <div
              key={activity.id}
              ref={isLast ? lastItemRef : undefined}
            >
              {isListMode ? (
                <ActivityFeedListItem
                  activity={activity}
                  isNew={newItemIds.has(activity.id)}
                  onAnimationEnd={clearNewItem}
                />
              ) : (
                <ActivityFeedItem
                  activity={activity}
                  isNew={newItemIds.has(activity.id)}
                  onAnimationEnd={clearNewItem}
                />
              )}
            </div>
          );
        })}
      </div>

      {isFetchingMore && (
        <div className="activity-feed__loading-more">
          <div className="activity-feed__spinner" />
          <span>Loading more activity...</span>
        </div>
      )}

      {!hasMore && activities.length > 0 && (
        <div className="activity-feed__end">
          You've reached the end of the activity feed.
        </div>
      )}
    </>
  );
};

export default ActivityFeed;
